/**
 * ExerciseVideoModal - Modal for displaying exercise videos
 * Created by neo on 23.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes, css, Global } from '@emotion/react';
import { Modal } from 'reactstrap';
import { ExerciseVariation } from '../../Model/ProgramPortfolio/ExerciseVariation';
import { MediaOverlay } from '../../Model/Media/MediaOverlay';
import { getOverlayStartSeconds, getOverlayText } from '../../Model/Media/MediaOverlay';
import { KinasticVideoPlayer } from '../EntryView/KinasticVideoPlayer';
import { useLanguage } from '../../Hooks/useLanguage';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
`;

const globalModalStyles = css`
  .exercise-video-modal .modal-dialog {
    max-width: 1400px !important;
    width: 95vw !important;
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    background: ${Theme.Colors.charcoal};
    border: none;
    border-radius: 16px;
    overflow: hidden;
  }
`;

const ModalContainer = styled.div`
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const ContentContainer = styled.div`
  padding: 0;
`;

const MainContent = styled.div<{ $hasOverlay: boolean }>`
  display: flex;
  flex-direction: ${({ $hasOverlay }) => ($hasOverlay ? 'row' : 'column')};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const VideoWrapper = styled.div<{ $hasOverlay: boolean }>`
  position: relative;
  width: 100%;
  background: #000;
  flex: ${({ $hasOverlay }) => ($hasOverlay ? '1 1 65%' : '1')};
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
`;

const OverlaySidebar = styled.div<{ $visible: boolean }>`
  flex: 0 0 280px;
  max-width: 320px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, ${Theme.Colors.charcoal} 100%);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  opacity: ${({ $visible }) => ($visible ? 1 : 0.5)};
  transition: opacity 0.3s ease;
  max-height: 500px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex: none;
    max-width: none;
    width: 100%;
    max-height: 200px;
    padding: 16px;
  }
`;

const OverlaySidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 2px solid ${Theme.Colors.primary};
`;

const OverlayIcon = styled.span`
  font-size: 1.2rem;
`;

const OverlayTitle = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 700;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const OverlayList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const OverlayItem = styled.div<{ $delay: number }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ $delay }) => $delay}ms;
  opacity: 0;
`;

const DotContainer = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const OverlayText = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const OverlayEmpty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  text-align: center;
  padding: 24px;
`;

const InfoSection = styled.div`
  padding: 20px 24px;
  background: ${Theme.Colors.charcoal};
`;

const ExerciseTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 8px 0;
`;

const ExerciseType = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: ${Theme.Colors.primary};
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const BodyPartsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const BodyPartTag = styled.span`
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  font-size: 12px;
`;

const NoVideoMessage = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
`;

type OverlayContentProps = {
  overlays: MediaOverlay[];
  currentTime: number;
  exerciseName: string;
  language: string;
};

function OverlaySidebarComponent({
  overlays,
  currentTime,
  language,
}: OverlayContentProps) {
  const visibleOverlays = useMemo(
    () => overlays.filter((o) => currentTime >= getOverlayStartSeconds(o)),
    [overlays, currentTime],
  );

  const hasVisibleOverlays = visibleOverlays.length > 0;

  return (
    <OverlaySidebar $visible={hasVisibleOverlays}>
      <OverlaySidebarHeader>
        <OverlayIcon>💡</OverlayIcon>
        <OverlayTitle>Instructions</OverlayTitle>
      </OverlaySidebarHeader>
      {hasVisibleOverlays ? (
        <OverlayList>
          {visibleOverlays.map((overlay, index) => (
            <OverlayItem key={`${overlay.startTime}-${index}`} $delay={index * 100}>
              <DotContainer>
                <Dot />
              </DotContainer>
              <OverlayText>{getOverlayText(overlay, language)}</OverlayText>
            </OverlayItem>
          ))}
        </OverlayList>
      ) : (
        <OverlayEmpty>Instructions will appear as the video plays...</OverlayEmpty>
      )}
    </OverlaySidebar>
  );
}

const OverlaySidebarContent = React.memo(OverlaySidebarComponent);

export type ExerciseVideoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  exercise: ExerciseVariation | null;
};

export const ExerciseVideoModal: React.FC<ExerciseVideoModalProps> = observer(
  ({ isOpen, onClose, exercise }) => {
    const { language } = useLanguage();
    const { t } = useTrans();
    const modalRef = React.useRef(null);
    const [currentTime, setCurrentTime] = useState(0);

    const handleProgress = useCallback((playedSeconds: number) => {
      setCurrentTime(playedSeconds);
    }, []);

    if (!exercise) return null;

    const title = exercise.localeName(language);
    const videos = exercise.medias.filter((m) => m.mediaType?.startsWith('video'));
    const bodyParts = exercise.bodyParts;
    const videoMedia = exercise.videoMedia;
    const overlays = videoMedia?.overlay ?? [];

    const hasOverlays = overlays.length > 0;

    return (
      <>
        <Global styles={globalModalStyles} />
        <StyledModal isOpen={isOpen} toggle={onClose} centered nodeRef={modalRef} modalClassName="exercise-video-modal">
          <div ref={modalRef}>
          <ModalContainer>
            <CloseButton onClick={onClose}>×</CloseButton>
            <ContentContainer>
              <MainContent $hasOverlay={hasOverlays}>
                <VideoWrapper $hasOverlay={hasOverlays}>
                  <VideoContainer>
                    {videos.length > 0 ? (
                      <KinasticVideoPlayer
                        videos={videos}
                        showLanguageSelector
                        hideFullscreenButton={false}
                        onProgress={handleProgress}
                      />
                    ) : (
                      <NoVideoMessage>
                        {t('exercise.noVideo', { defaultValue: 'No video available for this exercise' })}
                      </NoVideoMessage>
                    )}
                  </VideoContainer>
                </VideoWrapper>
                {hasOverlays && (
                  <OverlaySidebarContent
                    overlays={overlays}
                    currentTime={currentTime}
                    exerciseName={title}
                    language={language}
                  />
                )}
              </MainContent>
              <InfoSection>
                <ExerciseTitle>{title}</ExerciseTitle>
                <ExerciseType>{exercise.type}</ExerciseType>
                {bodyParts.length > 0 && (
                  <BodyPartsContainer>
                    {bodyParts.map((bp) => (
                      <BodyPartTag key={bp.id}>{bp.names.get(language) || bp.name}</BodyPartTag>
                    ))}
                  </BodyPartsContainer>
                )}
              </InfoSection>
            </ContentContainer>
          </ModalContainer>
          </div>
        </StyledModal>
      </>
    );
  },
);
